import { useEffect, useRef } from 'react';

import { IdentityUserAri } from '@atlassian/ari/identity/user';

import { fetchCommentById } from '@confluence/comments-data-manager/entry-points/useCommentById.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { fetchReactedActors } from '@confluence/reactions/entry-points/fetchReactedActors.ts';

import { restoreOptimisticAgentReplies } from './optimisticAgentReplyReconciliation.ts';
import { AGENT_EYES_EMOJI_ID } from './agentMentionConstants.ts';
import {
	useReactionRefetchRequestTracker,
	type ReactionRefetchRequestTracker,
} from './useReactionRefetchRequestTracker.ts';

import {
	EARLY_REACTION_REFETCH_DELAYS_MS,
	useCommentAgentReactionRefresh,
} from './commentAgentReactionRefresh.ts';

type UseRefetchReactionsAfterAgentReactionProps = {
	cloudId: string;
	contentId: string;
	commentId: string | undefined;
	requestTracker?: ReactionRefetchRequestTracker;
	onRefetch?: (comment: Awaited<ReturnType<typeof fetchCommentById>>) => void;
};

/** Refreshes a settled reaction write independently of agent-session discovery and transitions. */
export function useRefetchReactionsAfterAgentReaction({
	cloudId,
	contentId,
	commentId,
	onRefetch,
	requestTracker,
}: UseRefetchReactionsAfterAgentReactionProps): void {
	const privateTracker = useReactionRefetchRequestTracker(cloudId, contentId, commentId);
	const tracker = requestTracker ?? privateTracker;
	const refresh = useCommentAgentReactionRefresh(cloudId, contentId, commentId ?? '');
	const onRefetchRef = useRef(onRefetch);
	onRefetchRef.current = onRefetch;

	useEffect(() => {
		if (
			!refresh ||
			!commentId ||
			!cloudId ||
			!contentId ||
			Date.now() - refresh.startedAt > 14000 ||
			!expValEquals('cc_ai_agent_mention_from_comment', 'isEnabled', true) ||
			!expValEquals('agentic_reactions_master_exp', 'isEnabled', true) ||
			!fg('confluence_use_agentic_reactions_endpoint')
		) {
			return;
		}

		let cancelled = false;
		const timers: ReturnType<typeof setTimeout>[] = [];
		const cancel = () => {
			cancelled = true;
			timers.forEach(clearTimeout);
		};
		const refetch = async () => {
			const token = tracker.issue();
			try {
				const comment = await fetchCommentById({
					commentId,
					contentId,
					fetchPolicy: 'network-only',
				});
				// The response also replaces this thread's replies.
				restoreOptimisticAgentReplies();
				if (!comment || cancelled || !tracker.acceptResponse(token)) {
					return;
				}
				onRefetchRef.current?.(comment);
				if (
					!comment.reactionsSummary?.reactionsSummaryForEmoji.some(
						(reaction) => reaction?.emojiId === AGENT_EYES_EMOJI_ID && reaction.count > 0,
					)
				) {
					return;
				}
				const { actors } = await fetchReactedActors(cloudId, {
					contentId: commentId,
					contentType: 'COMMENT',
					containerId: contentId,
					containerType: 'PAGE',
					emojiId: AGENT_EYES_EMOJI_ID,
				});
				if (
					!cancelled &&
					tracker.isLatest(token) &&
					actors.some((actor) => {
						if (!actor?.agentId) {
							return false;
						}
						try {
							return IdentityUserAri.parse(actor.agentId).userId === refresh.agentIdentityAccountId;
						} catch {
							return actor.agentId === refresh.agentIdentityAccountId;
						}
					})
				) {
					cancel();
				}
			} catch {
				// Keep displayed reactions and allow the remaining bounded attempts to run.
			}
		};

		// Read immediately on settlement or late mount; retries keep their original deadlines.
		void refetch();
		const elapsed = Date.now() - refresh.startedAt;
		for (const delay of EARLY_REACTION_REFETCH_DELAYS_MS) {
			if (delay > elapsed) {
				timers.push(setTimeout(() => void refetch(), delay - elapsed));
			}
		}
		return cancel;
	}, [refresh, cloudId, commentId, contentId, tracker]);
}
